import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WELCOME = "Hi, I'm Ivy — Ivette's AI assistant. Ask me about services, experience, or how she can help your business. What brings you here today?";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Restore prior session id from local storage
    const sid = localStorage.getItem("radi_chat_session");
    if (sid) setSessionId(sid);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      const { session_id, reply } = res.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        localStorage.setItem("radi_chat_session", session_id);
      }
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry — I had trouble connecting. Please try again, or email Ivette directly at iradimorad@gmail.com." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "What services do you offer?",
    "Tell me about your experience",
    "Do you work remotely?",
  ];

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          className="chat-launcher"
          aria-label="Open chat"
          onClick={() => setOpen(true)}
        >
          <MessageCircle size={22} strokeWidth={2.2} />
          <span>Chat with Ivy</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="chat-panel" role="dialog" aria-label="Chat with Ivy">
          <div className="chat-header">
            <div className="chat-avatar">
              <img src="/brand/radi-financial-logo.png" alt="Radi Financial" />
            </div>
            <div className="chat-title">
              <b>Ivy · Radi Financial Assistant</b>
              <span><span className="chat-dot" />Usually replies instantly</span>
            </div>
            <button className="chat-close" aria-label="Close chat" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </div>

          <div className="chat-body" ref={scrollRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg ${m.role}`}>
                <div className="chat-bubble">{m.content}</div>
              </div>
            ))}
            {loading && (
              <div className="chat-msg assistant">
                <div className="chat-bubble chat-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div className="chat-suggestions">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => { setInput(s); }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form className="chat-input" onSubmit={send}>
            <input
              type="text"
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <button type="submit" aria-label="Send" disabled={loading || !input.trim()}>
              {loading ? <Loader2 size={16} className="chat-spin" /> : <Send size={16} />}
            </button>
          </form>
          <div className="chat-foot">Powered by Claude · Not a substitute for professional advice</div>
        </div>
      )}
    </>
  );
}
